import React, { useEffect, useState } from 'react';
import {
  AuthContainer,
  AuthPrimaryButton,
} from '@client/components/AuthComponents';
import { Button } from '@client/components/ui/button';
import { Label } from '@client/components/ui/label';
import { Input } from '@client/components/ui/input';
import { getOauthProviderIcon, selectSession } from '@client';
import { useDispatch, useSelector } from 'react-redux';
import { selectPreLogin } from '@client';
import { fetchAuth0ConnectionTypeByEmailForPublicRequest } from '@client';
import { isValidEmailFormat, SessionType } from '@shared';
import { useAuth0 } from '@auth0/auth0-react';
import { useHomeNavigation } from '@client/hooks/useHomeNavigation';
import { useSearchParams } from 'react-router-dom';
import { useAuthorizationParams } from '@client/hooks/useAuthorizationParams';
import { MaintenanceNotice } from '@/components/MaintenanceNotice';
import { getDomainFromEmail } from '@shared';
export interface LoginOptions {
  socialConnection:
    | Extract<Auth0Connection, 'google-oauth2' | 'windowslive'>
    | undefined;
  email?: string;
}

const SHOW_MAINTENANCE_NOTICE = false;

export function Login() {
  const [searchParams, setSearchParams] = useSearchParams();
  const emailHint = searchParams.get('email_hint');
  const screenHint = searchParams.get('screen_hint');
  const isSignup = screenHint === 'signup';
  const connectionParam = searchParams.get('connection');
  const overrideConnection = searchParams.get('override_connection');

  const [email, setEmail] = useState<string>(emailHint ?? '');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [warningState, setWarningState] = useState<{
    show: boolean;
    email: string;
  }>({ show: false, email: '' });

  const {
    status,
    error,
    connection,
    phoneNumber,
    email: preLoginEmail,
  } = useSelector(selectPreLogin);
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const {
    authorizationParams,
    searchParams: { clientSignIn, redirectTo },
  } = useAuthorizationParams();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValidEmail =
      !!(email && email.length > 0) && isValidEmailFormat(email);
    setIsValidEmail(isValidEmail);
    if (!isValidEmail) {
      return;
    }
    dispatch(fetchAuth0ConnectionTypeByEmailForPublicRequest(email));
  };

  const handleSocialLogin = (
    socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'>,
  ) => {
    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        connection: socialConnection,
      },
    });
  };

  const handleSignUpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('screen_hint', 'signup');
      return newParams;
    });
  };

  const handleSignInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('screen_hint'); // Remove screen_hint to go back to login
      return newParams;
    });
  };

  // Trigger Auth0 login
  useEffect(() => {
    if (
      status === 'loading' ||
      session.sessionType !== SessionType.unauthenticated
    ) {
      return;
    }
    if (clientSignIn) {
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          ...(connectionParam && { connection: connectionParam }),
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }
    if (status === 'complete' && connection) {
      const finalEmail = preLoginEmail ?? email;
      const isInternalUser =
        getDomainFromEmail(finalEmail) === 'joinwaterlily.com';
      const shouldUseEmail = isInternalUser || overrideConnection === 'email';
      const defaultConnection = shouldUseEmail ? 'email' : 'sms';
      const finalConnection =
        connection === 'none' ? defaultConnection : connection;

      if (connection === 'none' && !isSignup) {
        setWarningState({ show: true, email: finalEmail });
        return;
      }
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': finalConnection,
          'login_hint': finalConnection === 'sms' ? phoneNumber : finalEmail,
          'ext-email': finalConnection === 'sms' ? finalEmail : undefined,
        },
      });
    }
  }, [
    status,
    connection,
    authorizationParams,
    emailHint,
    clientSignIn,
    isLoading,
    isAuthenticated,
    phoneNumber,
    email,
    isSignup,
  ]);

  useHomeNavigation();

  return (
    <AuthContainer
      title={isSignup ? 'Create an account' : 'Welome back Advisor'}
    >
      {SHOW_MAINTENANCE_NOTICE && (
        <MaintenanceNotice
          message={
            'Some features may be temporarily unavailable due to scheduled maintenance. We’re working to restore full functionality as quickly as possible.'
          }
          startTime={''}
          endTime={''}
        />
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-6 pt-4 text-gray-800"
      >
        {/* Social Login Buttons */}
        {isSignup && (
          <>
            <div className="flex flex-col gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full py-6 text-base font-normal"
                onClick={() => handleSocialLogin('google-oauth2')}
              >
                {getOauthProviderIcon('google')}
                Continue with Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full py-6 text-base font-normal"
                onClick={() => {
                  handleSocialLogin('windowslive');
                }}
              >
                {getOauthProviderIcon('microsoft')}
                Continue with Microsoft
              </Button>
            </div>

            <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="text-muted-foreground relative z-10 bg-white px-2 text-primary-dark">
                Or continue with
              </span>
            </div>
          </>
        )}

        <div className="grid gap-3">
          <Label
            htmlFor="email"
            className="text-primary-dark"
          >
            {isSignup ? 'Email' : 'Email*'}
          </Label>

          <Input
            id="email"
            type="email"
            placeholder="advisor@example.com"
            value={email}
            className="rounded-full px-4 py-6 text-base"
            onChange={e => {
              setEmail(e.target.value);
              setWarningState({ show: false, email: '' });
            }}
            required
          />
          {!isValidEmail && (
            <p className="text-sm text-red-500">
              Please enter a valid email address
            </p>
          )}
        </div>

        {error && <ErrorMessage error={error} />}

        {warningState.show && (
          <div className="flex items-center gap-2 rounded-2xl  bg-amber-50 p-3 text-sm text-amber-800">
            <p className="text-sm leading-relaxed">
              No account found for{' '}
              <span className="font-semibold">{warningState.email}</span>. Click{' '}
              <span className="font-medium">Sign Up</span> to create a new
              account.
            </p>
          </div>
        )}

        <AuthPrimaryButton
          title={isSignup ? 'Create Account' : 'Continue to Sign In'}
          onClick={undefined} // Will be handled by form submission
          className="py-6 text-base"
          type="submit"
          isLoading={status === 'loading'}
        />

        {isSignup ? (
          <div className="text-center text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={handleSignInClick}
              className="font-semibold text-primary-dark hover:text-primary-dark/80"
            >
              Sign in
            </button>
          </div>
        ) : (
          <div className="text-center text-sm">
            Don&apos;t have an account?{' '}
            <button
              type="button"
              onClick={handleSignUpClick}
              className="font-semibold text-primary-dark hover:text-primary-dark/80"
            >
              Sign up
            </button>
          </div>
        )}
      </form>
    </AuthContainer>
  );
}

function ErrorMessage({ error }: { error: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
      <svg
        className="h-4 w-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      </svg>
      <span className="font-medium">
        {error || 'Something went wrong. Please try again.'}
      </span>
    </div>
  );
}
